import React, { SetStateAction, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { FrontendUserInfo, OrganizationInfo } from "node-library/dist/portal";

import { Organization, User } from "node-library/dist/database";

import {
	OrganizationTableSortColumn,
	SortOrder,
	UserTableSortColumn,
} from "node-library/dist/paginations";
import { useSnackbar } from "../../../common-components/toast/showToastMessage";
import { Severity } from "node-library/dist/constants";
import { sendPOSTRequest } from "../../../requestHelper/sendRequest";
import {
	RedErrorIcon,
	RedTrashIcon,
} from "../../../common-components/toast/errorIcons";
import { getOrganizationSortString } from "../../../helpers/organizationHelper";
import {
	CustomSearchBar,
	RowDropdown,
} from "../../../common-components/inputs";
import { SortArrowIcon } from "../../../common-components/svgIcons";
import { Pagination } from "@mui/material";
import { NeutralColor, SecondaryColor } from "node-library/dist/colors";
import { GreenTickIcon } from "../../../common-components/toast/successIcons";
import ViewOrderPageSkeletonUI from "../viewOrders/skeletonUI/orderHistoryPageSkeleton";
import OrganizationContent from "./organizationContent";
import OrganizationDetailsContent from "./organizationDetailsContent";
import { YellowCrossIcon } from "../../../common-components/toast/warnIcons";
import { BlueButton } from "../../../common-components/button";
import AddIcon from "@mui/icons-material/Add";
import NewOrganizationModal from "../../../common-components/modals/newOrganizationModal";
import DeleteOrganizationConfirmModal from "../../../common-components/modals/deleteOrganizationConfirmModal";
import RemoveUserFromOrganizationModal from "../../../common-components/modals/removeUserFromOrganizationModal";

interface Props {
	setUnsavedChanges: React.Dispatch<SetStateAction<boolean>>;
	frontendUser: FrontendUserInfo | undefined;
}

export default function OrganizationPage(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const setUnsavedChanges = props.setUnsavedChanges;

	const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);

	const [organizations, setOrganizations] = useState<Array<OrganizationInfo>>(
		[]
	);
	const [organizationUsers, setOrganizationUsers] = useState<
		Array<FrontendUserInfo>
	>([]);

	const [selectedOrganization, setSelectedOrganization] =
		useState<Organization>();

	const [loading, setLoading] = useState<boolean>(false);
	const [adminInAction, setAdminInAction] = useState<boolean>(false);
	const [searchText, setSearchText] = useState<string>("");

	//paginations
	const [totalPageCount, setTotalPageCount] = useState<number>(0);
	const [totalUserPageCount, setTotalUserPageCount] = useState<number>(0);
	const [sortBy, setSortBy] = useState<UserTableSortColumn>(
		UserTableSortColumn.INSERTED
	);
	const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.DESC);
	const [currentPagination, setCurrentPagination] = useState<number>(1);
	const [removeUserModalConfirmOpen, setRemoveUserModalConfirmOpen] =
		useState<boolean>(false);
	const [newOrganizationModalOpen, setNewOrganizationModalOpen] =
		useState<boolean>(false);

	//confirm modals
	const [discardOrganizationConfirmOpen, setDiscardOrganizationConfirmOpen] =
		useState<boolean>(false);
	const [proceedAction, setProceedAction] = useState<() => void>(
		() => () => {}
	);

	function toggleSortOrder() {
		if (sortOrder === SortOrder.ASC) {
			setSortOrder(SortOrder.DESC);
		} else {
			setSortOrder(SortOrder.ASC);
		}
	}

	async function getOrganizations(searchText: string) {
		setLoading(true);
		try {
			const response = await sendPOSTRequest(
				"/organizations/get-organizations",
				{
					searchText,
					sortBy,
					sortOrder,
					pageNumber: currentPagination,
				}
			);

			const organizations: Array<OrganizationInfo> =
				response?.data?.organizations;
			const pageCount: number = response?.data?.totalPages;
			setOrganizations(organizations);
			setTotalPageCount(pageCount);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		} finally {
			setLoading(false);
		}
	}

	async function getOrganizationUsers(organization: Organization) {
		setLoading(true);
		try {
			const response = await sendPOSTRequest(
				"/organizations/get-organization-users",
				{
					organization,
				}
			);

			const users: Array<FrontendUserInfo> = response?.data?.users;
			setOrganizationUsers(users);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		} finally {
			setLoading(false);
		}
	}

	async function deleteOrganization(organization: OrganizationInfo) {
		//send a request to delete the temp order in the backend
		try {
			const response = await sendPOSTRequest(
				"/organizations/delete-organization",
				{
					organizationId: organization.organizationId,
				}
			);
			//go to the submitted order page
			openSnackbar(
				translation("OrganizationDeleteSuccess"),
				Severity.WARN,
				undefined,
				<YellowCrossIcon />
			);
			await goBackToViewOrganizationsPage(true);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		}
	}

	async function removeUser(
		organization: OrganizationInfo,
		user: FrontendUserInfo
	) {
		try {
			const response = await sendPOSTRequest(
				"/organizations/remove-user-from-organization",
				{
					userName: user.userName,
					organizationId: undefined,
				}
			);
			openSnackbar(
				translation("UserAccessRemoved"),
				Severity.ERROR,
				undefined,
				<RedTrashIcon />
			);

			setUnsavedChanges(false);
			await gotoOrganizationDetailsPage(organization);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		}
	}
	async function addUser(
		organization: OrganizationInfo,
		organizationUser: FrontendUserInfo
	) {
		try {
			const response = await sendPOSTRequest(
				"/organizations/add-user-to-organization",
				{
					userName: organizationUser.userName,
					organizationId: organization.organizationId,
				}
			);
			openSnackbar(
				translation("UserAccessAdded"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />
			);

			setUnsavedChanges(false);
			await gotoOrganizationDetailsPage(organization);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		}
	}

	const addOrganizationButton: React.ReactNode = (
		<BlueButton
			key={`add-organization`}
			id={"add-organization-button"}
			onClick={() => {
				setNewOrganizationModalOpen(true);
			}}
			disabled={adminInAction}
			buttonClassName="button-with-left-icon"
		>
			<AddIcon />
			{translation("Organization")}
		</BlueButton>
	);

	const sortOptionSelection: React.ReactNode = (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				gap: "5px",
				marginLeft: "auto",
			}}
		>
			<div
				onClick={() => {
					toggleSortOrder();
				}}
				style={{ cursor: "pointer" }}
			>
				<SortArrowIcon sortOrder={sortOrder} />
			</div>
			<RowDropdown
				id="organization-sort-organization-dropdown"
				className="sort-organization-dropdown"
				label={"a"}
				labelClassName="hide"
				options={Object.values(OrganizationTableSortColumn).map((value) => {
					return {
						value: value,
						title: getOrganizationSortString(value, translation),
					};
				})}
				value={sortBy}
				setValue={setSortBy}
				noBorder
				backgroundColor="transparent"
			></RowDropdown>
		</div>
	);

	useEffect(() => {
		getOrganizations("");
	}, [currentPagination, sortBy, sortOrder]);

	function gotoOrganizationDetailsPage(organization: Organization) {
		setSelectedOrganization(organization);
		setCurrentPageIndex(1);
		getOrganizationUsers(organization);
	}
	function goBackToViewOrganizationsPage(reload: boolean) {
		setCurrentPageIndex(0);
		setSelectedOrganization(undefined);
		if (reload) {
			getOrganizations(searchText);
		}
	}
	function goToEditOrganizationPage(organization: OrganizationInfo) {
		setSelectedOrganization(organization);
		setCurrentPageIndex(2);
	}

	let currentComponent: JSX.Element = <></>;
	switch (currentPageIndex) {
		case 0:
			currentComponent = (
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						gap: "20px",
						minHeight: "calc(100vh - 140px)",
						marginTop: "60px",
					}}
				>
					<div
						style={{
							flex: "1",
							display: "flex",
							flexDirection: "column",
							gap: "20px",
						}}
					>
						<div className="view-organizations-page-header">
							<div className="typography-heading-01">
								{translation("Organization")}
							</div>
							<CustomSearchBar
								id="view-organization-page-searchbar"
								searchText={searchText}
								setSearchText={setSearchText}
								placeholder={translation("SearchByCompanyName")}
								searchButtonText={translation("Search")}
								onSearch={getOrganizations}
							></CustomSearchBar>
						</div>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								flexWrap: "wrap",
								gap: "10px",
							}}
						>
							{addOrganizationButton}

							{sortOptionSelection}
						</div>

						{loading ? (
							<ViewOrderPageSkeletonUI />
						) : (
							<OrganizationContent
								organizations={organizations}
								user={props.frontendUser}
								goToViewOrganizationDetails={gotoOrganizationDetailsPage}
								setDiscardOrganizationConfirmOpen={
									setDiscardOrganizationConfirmOpen
								}
								setProceedAction={setProceedAction}
								deleteOrganization={deleteOrganization}
								goToEditOrganizationPage={goToEditOrganizationPage}
							/>
						)}
					</div>
					{!loading && organizations.length > 0 && (
						<div
							style={{
								display: "flex",
								justifyContent: "center",
							}}
						>
							<Pagination
								count={totalPageCount}
								page={currentPagination}
								onChange={(e, value) => {
									setCurrentPagination(value);
								}}
								sx={{
									backgroundColor: "white",
									borderRadius: "30px",
									padding: "6px 16px",

									"& .MuiPaginationItem-root": {
										backgroundColor: "white",
										color: "black",
										border: "1px solid transparent", // Hide outline when not selected
										"&:hover": {
											backgroundColor: "#f5f5f5", // Change background on hover
										},
										borderRadius: "50%",
									},
									"& .Mui-selected": {
										backgroundColor: SecondaryColor.SECONDARY_50, // Change background for selected item
										border: `1px solid ${NeutralColor.NEUTRAL_GREY_900}`, // Change text color for selected item
									},
								}}
								variant="outlined"
								shape="rounded"
							/>
						</div>
					)}
				</div>
			);
			break;
		case 1:
			currentComponent = (
				<OrganizationDetailsContent
					organization={selectedOrganization as OrganizationInfo}
					goBackToViewOrganizationsPage={goBackToViewOrganizationsPage}
					gotoOrganizationDetailsPage={gotoOrganizationDetailsPage}
					adminInAction={adminInAction}
					frontendUser={props.frontendUser}
					// setDeleteUserConfirmOpen={setDeleteUserConfirmOpen}
					proceedAction={proceedAction}
					setProceedAction={setProceedAction}
					setUnsavedChanges={setUnsavedChanges}
					// getOrganizationUsers={getOrganizationUsers}
					organizationUsers={organizationUsers}
					removeUser={removeUser}
					addUser={addUser}
					setRemoveUserModalConfirmOpen={setRemoveUserModalConfirmOpen}
					// currentUserPagination={currentUserPagination}
					// setCurrentUserPagination={setCurrentUserPagination}
					totalUserPageCount={totalUserPageCount}
					loading={loading}
				></OrganizationDetailsContent>
			);
			break;
	}

	return (
		<div style={{ margin: "16px 24px 16px" }}>
			{currentComponent}

			{
				<NewOrganizationModal
					open={newOrganizationModalOpen}
					setOpen={setNewOrganizationModalOpen}
					proceedAction={proceedAction}
					setUnsavedChanges={setUnsavedChanges}
					goBackToViewOrganizationsPage={goBackToViewOrganizationsPage}
				></NewOrganizationModal>
			}
			{
				<DeleteOrganizationConfirmModal
					open={discardOrganizationConfirmOpen}
					setOpen={setDiscardOrganizationConfirmOpen}
					proceedAction={proceedAction}
				></DeleteOrganizationConfirmModal>
			}
			{
				<RemoveUserFromOrganizationModal
					open={removeUserModalConfirmOpen}
					setOpen={setRemoveUserModalConfirmOpen}
					proceedAction={proceedAction}
				></RemoveUserFromOrganizationModal>
			}
		</div>
	);
}
